import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { SiteLayout, useDocumentTitle } from '../components/platform/SiteLayout';
import { Container, Heading, Card, Btn, Spinner, ErrorState, PlainText } from '../components/platform/ui';
import useAsync from '../hooks/useAsync';
import { platformApi, errorText } from '../services/platformApi';
import VideoPlayer from '../components/platform/VideoPlayer';
import { useAuth } from '../contexts/AuthContext';
import { LLink } from '../lib/lang';

const LessonPage = () => {
  const { slug, lessonId } = useParams();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const id = parseInt(lessonId, 10);
  const { data: l, loading, error, reload, setData } = useAsync(() => platformApi.lesson(slug, id), [slug, id, user?.id, i18n.language]);
  const [busy, setBusy] = useState(false);
  useDocumentTitle(l?.title);

  const complete = async () => {
    setBusy(true);
    try {
      await platformApi.completeLesson(slug, id);
      setData({ ...l, completed: true });
      toast.success(t('pl.courses.markedDone'));
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  let content;
  if (loading) content = <Spinner />;
  else if (error) {
    const st = error.response?.status;
    content = st === 403 || st === 401 ? (
      <Card className="text-center py-10">
        <Lock size={36} className="mx-auto mb-3 text-gray-600" />
        <Heading level={2} className="text-2xl mb-2">{t('pl.courses.lessonUnavailable')}</Heading>
        <p className="text-gray-700 mb-5">{errorText(error, t('pl.courses.lessonLocked'))}</p>
        <Btn to={`/webinars/${slug}`}>{t('pl.courses.toCourse')}</Btn>
      </Card>
    ) : <ErrorState text={st === 404 ? t('pl.courses.lessonNotFound') : t('pl.courses.lessonLoadError')} onRetry={st === 404 ? undefined : reload} />;
  } else {
    content = (
      <article className="space-y-6">
        <div><LLink to={`/webinars/${slug}`} className="text-sm underline">{t('pl.courses.backToCourse')}</LLink>
          <Heading level={1} className="text-3xl sm:text-4xl mt-2">{l.position}. {l.title}</Heading></div>
        {l.video_url && <VideoPlayer url={l.video_url} title={l.title} />}
        {l.image_url && <img src={l.image_url} alt="" className="max-w-full border-2 border-black" />}
        {l.body && <PlainText text={l.body} />}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t-2 border-black">
          {l.prev_id ? <Btn variant="secondary" to={`/webinars/${slug}/lessons/${l.prev_id}`}><ChevronLeft size={16} />{t('pl.common.back')}</Btn> : <span />}
          {user && (l.completed
            ? <span className="flex items-center gap-2 text-green-700"><CheckCircle2 size={18} />{t('pl.courses.done')}</span>
            : <Btn variant="secondary" onClick={complete} loading={busy}>{t('pl.courses.markDone')}</Btn>)}
          {l.next_id ? <Btn to={`/webinars/${slug}/lessons/${l.next_id}`}>{t('pl.common.next')}<ChevronRight size={16} /></Btn> : <span />}
        </div>
      </article>
    );
  }
  return <SiteLayout><Container className="py-10 max-w-3xl">{content}</Container></SiteLayout>;
};

export default LessonPage;
